// src/components/DamPreviewDialog/index.tsx
// Просмотрщик файлов DAM-хранилища (демо). Скачивание не заменяет — дополняет:
// в шапке остаётся кнопка «Скачать» (downloadDamFile, как в списках).
// Источник контента:
//   • upload-* → реальный Blob из IndexedDB → pdf/iframe, docx/mammoth, xlsx/SheetJS, текст;
//   • мок/сгенерированные → getDamFileContent → текстовые секции (markdown) / таблица.
// Неподдержанные форматы (pptx и т.п.) → заглушка с кнопкой «Скачать».
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Dialog, IconButton, Typography, CircularProgress, Tooltip } from '@mui/material';
import { Download, X, FileText, AlertCircle } from 'lucide-react';
import type { DamFile } from 'mocks/dam';
import type { DamFileContent } from 'mocks/damContents';
import { getDamFileContent } from 'services/demoApi/damApi';
import { getUploadedBlob } from 'services/demoApi/damUploads';
import { downloadDamFile } from 'services/demoApi/fileDownload';
import { ChatMarkdown } from 'components/ChatMarkdown';

type PreviewKind = 'pdf' | 'image' | 'docx' | 'sheet' | 'text' | 'doc' | 'table' | 'unsupported';

interface SheetData {
  names: string[];
  htmlByName: Record<string, string>;
}

interface RenderState {
  kind: PreviewKind;
  /** object URL для pdf/image */
  objectUrl?: string;
  /** для текста загруженного файла */
  text?: string;
  /** для docx (mammoth html) */
  html?: string;
  /** для xlsx загруженного файла */
  sheet?: SheetData;
  /** для мок/сгенерированного контента DAM */
  content?: DamFileContent;
}

/** Лёгкая защита: вырезаем <script>, обработчики on*= и javascript: из HTML mammoth. */
function sanitizeHtml(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/javascript:/gi, '');
}

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];

/** Рендер реального Blob загруженного файла по расширению. */
async function renderUploadedBlob(blob: Blob, ext: string): Promise<RenderState> {
  if (ext === 'pdf') {
    return { kind: 'pdf', objectUrl: URL.createObjectURL(blob) };
  }
  if (IMAGE_EXTS.includes(ext)) {
    return { kind: 'image', objectUrl: URL.createObjectURL(blob) };
  }
  if (ext === 'docx') {
    const arrayBuffer = await blob.arrayBuffer();
    const mammothMod = await import('mammoth');
    const mammoth = mammothMod.default ?? mammothMod;
    const { value } = await mammoth.convertToHtml({ arrayBuffer });
    return { kind: 'docx', html: sanitizeHtml(value) };
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    const arrayBuffer = await blob.arrayBuffer();
    const XLSX = await import('xlsx');
    const wb = XLSX.read(arrayBuffer, { type: 'array' });
    const htmlByName: Record<string, string> = {};
    wb.SheetNames.forEach((n) => {
      htmlByName[n] = XLSX.utils.sheet_to_html(wb.Sheets[n], { editable: false });
    });
    return { kind: 'sheet', sheet: { names: wb.SheetNames, htmlByName } };
  }
  if (['txt', 'md', 'json', 'log', 'xml', 'yaml', 'yml'].includes(ext)) {
    return { kind: 'text', text: await blob.text() };
  }
  return { kind: 'unsupported' };
}

interface DamPreviewDialogProps {
  open: boolean;
  file: DamFile | null;
  onClose: () => void;
}

export default function DamPreviewDialog({ open, file, onClose }: DamPreviewDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [render, setRender] = useState<RenderState | null>(null);
  const [activeSheet, setActiveSheet] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [createdUrl, setCreatedUrl] = useState<string | null>(null);

  // Чистим object URL при смене файла / закрытии.
  useEffect(() => {
    return () => {
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [createdUrl]);

  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;

    setIsLoading(true);
    setError(null);
    setRender(null);
    setActiveSheet(null);

    (async () => {
      try {
        let next: RenderState;
        // Загруженные файлы — показываем оригинальный Blob (максимальная точность).
        if (file.id.startsWith('upload-')) {
          const blob = await getUploadedBlob(file.id);
          if (blob) {
            next = await renderUploadedBlob(blob, file.ext);
          } else {
            // Фолбэк на извлечённый текст, если Blob недоступен.
            const content = await getDamFileContent(file.id);
            next = content
              ? { kind: content.kind === 'table' ? 'table' : 'doc', content }
              : { kind: 'unsupported' };
          }
        } else {
          // Мок/сгенерированные файлы — рендерим структурированный контент.
          const content = await getDamFileContent(file.id);
          next = content
            ? { kind: content.kind === 'table' ? 'table' : 'doc', content }
            : { kind: 'unsupported' };
        }

        if (cancelled) {
          if (next.objectUrl) URL.revokeObjectURL(next.objectUrl);
          return;
        }
        if (next.objectUrl) setCreatedUrl(next.objectUrl);
        if (next.kind === 'sheet') setActiveSheet(next.sheet?.names[0] ?? null);
        setRender(next);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Не удалось открыть файл');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, file]);

  const handleDownload = useCallback(async () => {
    if (!file) return;
    setDownloading(true);
    try {
      await downloadDamFile(file);
    } catch {
      // молча — скачивание дублируется кнопкой в списке
    } finally {
      setDownloading(false);
    }
  }, [file]);

  const sheetTableHtml = useMemo(() => {
    if (render?.kind !== 'sheet' || !render.sheet || !activeSheet) return '';
    return render.sheet.htmlByName[activeSheet] ?? '';
  }, [render, activeSheet]);

  const fileName = file ? `${file.name}.${file.ext}` : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        <FileText size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
        <Typography
          sx={{
            flex: 1,
            fontSize: 14,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
          }}
        >
          {fileName}
        </Typography>
        <Tooltip title="Скачать">
          <span>
            <IconButton size="small" onClick={() => void handleDownload()} disabled={downloading}>
              {downloading ? <CircularProgress size={14} /> : <Download size={16} />}
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose} aria-label="Закрыть">
          <X size={16} />
        </IconButton>
      </Box>

      {/* Sheet tabs */}
      {render?.kind === 'sheet' && render.sheet && render.sheet.names.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            overflowX: 'auto',
            flexShrink: 0,
          }}
        >
          {render.sheet.names.map((n) => (
            <Box
              key={n}
              component="button"
              type="button"
              onClick={() => setActiveSheet(n)}
              sx={{
                px: 1.25,
                py: 0.5,
                border: '1px solid',
                borderColor: n === activeSheet ? 'primary.main' : 'divider',
                bgcolor: n === activeSheet ? (t) => `${t.palette.primary.main}14` : 'transparent',
                color: n === activeSheet ? 'primary.main' : 'text.secondary',
                borderRadius: 1,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {n}
            </Box>
          ))}
        </Box>
      )}

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, position: 'relative', bgcolor: '#f8fafc' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              color: 'text.secondary',
            }}
          >
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: 13 }}>Загружаем файл…</Typography>
          </Box>
        )}

        {!isLoading && error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              px: 3,
              textAlign: 'center',
            }}
          >
            <AlertCircle size={28} color="#dc2626" />
            <Typography sx={{ fontSize: 14, color: 'text.primary' }}>{error}</Typography>
          </Box>
        )}

        {!isLoading && !error && render && (
          <Box sx={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
            {render.kind === 'pdf' && (
              <iframe
                title={fileName}
                src={render.objectUrl}
                style={{ width: '100%', height: '100%', border: 'none' }}
              />
            )}

            {render.kind === 'image' && (
              <Box
                sx={{
                  minHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                }}
              >
                <img
                  src={render.objectUrl}
                  alt={fileName}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              </Box>
            )}

            {render.kind === 'text' && (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 3,
                  fontSize: 13,
                  lineHeight: 1.6,
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: 'text.primary',
                  bgcolor: '#fff',
                  minHeight: '100%',
                }}
              >
                {render.text}
              </Box>
            )}

            {render.kind === 'docx' && (
              <Box
                className="file-preview-docx"
                sx={{
                  p: { xs: 3, md: 5 },
                  maxWidth: 880,
                  mx: 'auto',
                  bgcolor: '#fff',
                  minHeight: '100%',
                  fontSize: 14.5,
                  lineHeight: 1.7,
                  color: 'text.primary',
                  '& img': { maxWidth: '100%', height: 'auto' },
                  '& table': { borderCollapse: 'collapse', width: '100%', my: 1.5 },
                  '& td, & th': { border: '1px solid #d1d5db', px: 1, py: 0.5 },
                  '& h1, & h2, & h3': { lineHeight: 1.3, mt: 2, mb: 1 },
                  '& p': { my: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: render.html ?? '' }}
              />
            )}

            {render.kind === 'sheet' && (
              <Box
                className="file-preview-sheet"
                sx={{
                  p: 2,
                  bgcolor: '#fff',
                  minHeight: '100%',
                  '& table': { borderCollapse: 'collapse', fontSize: 13 },
                  '& td, & th': {
                    border: '1px solid #d1d5db',
                    px: 1,
                    py: 0.5,
                    whiteSpace: 'nowrap',
                  },
                  '& tr:first-of-type td': { fontWeight: 600, bgcolor: '#f1f5f9' },
                }}
                dangerouslySetInnerHTML={{ __html: sheetTableHtml }}
              />
            )}

            {render.kind === 'doc' && render.content?.kind === 'text' && (
              <Box
                sx={{
                  p: { xs: 3, md: 5 },
                  maxWidth: 880,
                  mx: 'auto',
                  bgcolor: '#fff',
                  minHeight: '100%',
                  fontSize: 14,
                  lineHeight: 1.7,
                  color: 'text.primary',
                }}
              >
                {render.content.sections.map((section, i) => (
                  <Box key={i} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                    {section.heading && (
                      <Typography
                        sx={{ fontSize: 16, fontWeight: 700, mb: 1, color: 'text.primary' }}
                      >
                        {section.heading}
                      </Typography>
                    )}
                    {section.bodyMd ? (
                      <ChatMarkdown content={section.bodyMd} />
                    ) : (
                      <Typography
                        component="div"
                        sx={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}
                      >
                        {section.body}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {render.kind === 'table' && render.content?.kind === 'table' && (
              <Box sx={{ p: 2, bgcolor: '#fff', minHeight: '100%', overflowX: 'auto' }}>
                <Box
                  component="table"
                  sx={{
                    borderCollapse: 'collapse',
                    fontSize: 13,
                    width: '100%',
                    '& td, & th': {
                      border: '1px solid #d1d5db',
                      px: 1,
                      py: 0.5,
                      textAlign: 'left',
                      verticalAlign: 'top',
                    },
                    '& th': { fontWeight: 700, bgcolor: '#f1f5f9' },
                  }}
                >
                  <thead>
                    <tr>
                      {render.content.headers.map((h, i) => (
                        <th key={i}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {render.content.rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          <td key={ci}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </Box>
            )}

            {render.kind === 'unsupported' && (
              <Box
                sx={{
                  minHeight: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  px: 3,
                  textAlign: 'center',
                }}
              >
                <FileText size={32} color="#9ca3af" />
                <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
                  Предпросмотр для этого формата недоступен
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  Скачайте файл, чтобы открыть его локально
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={() => void handleDownload()}
                  disabled={downloading}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    mt: 0.5,
                    px: 1.75,
                    py: 0.75,
                    border: 'none',
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                    opacity: downloading ? 0.5 : 1,
                  }}
                >
                  <Download size={14} /> Скачать
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
